import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryClient } from '@/lib/query-client';

import type { InspectionReportTemplate } from './use-combination-restrictions';

import { useCombinationRestrictions } from './use-combination-restrictions';

export function useInspectionReports(projectVersionId: string) {
    const { t } = useTranslation('applications');

    const inspectionReports = useSuspenseQuery(
        elektraAppQueryOptions.project.request.inspectionReportsListOptions({
            electricalProjectVersionId: projectVersionId,
        }),
    );

    // Transform API data to templates with inspection kind info
    const templates: InspectionReportTemplate[] = inspectionReports.data?.Templates.map(item => ({
        id: item.ElectricalProjectDocumentTemplateId!,
        name: item.Name!,
        inspectionKind: item.ElectricalInspectionKind!,
        selected: item.Selected ?? false,
    })) || [];

    // Apply combination restrictions
    const {
        options: inspectionOptions,
        isValidSelection,
        selectedKinds,
        disabledKinds,
    } = useCombinationRestrictions(templates);

    const updateTemplateMutation = api.useMutation('put', '/api/v2/electrical/projects/documents/templates/edit', {
        onError: (error) => {
            toast.error(
                error.Message || t('error.default'),
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries(
                elektraAppQueryOptions.project.request.inspectionReportsListOptions({
                    electricalProjectVersionId: projectVersionId,
                }),
            );
            queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({
                    electricalProjectVersionId: projectVersionId,
                }),
            );
        },
    });

    function handleTemplateChange(value: string) {
        const items = inspectionReports.data?.Templates.map(item => ({
            ElectricalProjectDocumentTemplateId: item.ElectricalProjectDocumentTemplateId!,
            Selected: item.ElectricalProjectDocumentTemplateId === value,
        }));

        updateTemplateMutation.mutate({
            body: {
                ElectricalProjectVersionId: projectVersionId,
                Templates: items,
            },
        });
    }

    function handleMultipleTemplatesChange(values: string[]) {
        const items = inspectionReports.data?.Templates.map(item => ({
            ElectricalProjectDocumentTemplateId: item.ElectricalProjectDocumentTemplateId!,
            Selected: values.includes(item.ElectricalProjectDocumentTemplateId!),
        }));

        updateTemplateMutation.mutate({
            body: {
                ElectricalProjectVersionId: projectVersionId,
                Templates: items,
            },
        });
    }

    return {
        inspectionOptions,
        handleTemplateChange,
        handleMultipleTemplatesChange,
        isLoading: inspectionReports.isLoading,
        isPending: updateTemplateMutation.isPending,
        isValidSelection,
        selectedKinds,
        disabledKinds,
    };
}
